<script lang="ts">
  // Mobile folder picker (components doc §1 `foldersSheet`): the rail's sections as big
  // buttons, with the same folder actions.
  import { badgeText, t } from '$lib/i18n';
  import { displayName, folderIcon, leafName } from '$lib/mail/folders';
  import { store } from '$lib/store';
  import Icon from '../ui/Icon.svelte';
  import Sheet from '../ui/Sheet.svelte';
  import { createArchiveFlow, deleteFolderFlow, folderSections, newFolderFlow, newSubfolderFlow, renameFolderFlow } from './_local';
  import { folderUi } from './folderUi.svelte';

  const sections = $derived(folderSections(store.folders, folderUi.collapsed, folderUi.moreFolders, store.currentFolder));

  function close() {
    store.foldersSheet = false;
  }
  function open(path: string) {
    close();
    void store.openFolder(path);
  }
  function after(p: Promise<unknown>) {
    close();
    void p;
  }
</script>

<Sheet label={t('folders')} onclose={close}>
  {#each sections.special as f (f.path)}
    {@const name = displayName(f, sections.ctx)}
    {@const on = store.currentFolder === f.path}
    <button type="button" class="fbtn big" class:active={on} aria-current={on ? 'page' : undefined} onclick={() => open(f.path)}>
      <span class="fi"><Icon name={folderIcon(f, sections.ctx)} size={20} /></span><span class="fn">{name}</span>
      {#if f.unseen}<span class="badge">{badgeText(f.unseen)}</span>{/if}
    </button>
  {/each}
  {#if !store.archivePath}
    <button type="button" class="fbtn big" onclick={() => after(createArchiveFlow())}><span class="fi"><Icon name="plus" size={20} /></span><span class="fn">{t('createArchive')}</span></button>
  {/if}
  {#if sections.custom.length}
    <div class="sheet-sep"></div>
    {#each sections.custom as n (n.folder.path)}
      {@const f = n.folder}
      {@const leaf = leafName(f.path, f.name, sections.delimiter)}
      {@const collapsed = folderUi.collapsed.has(f.path)}
      {@const on = store.currentFolder === f.path}
      <div class="frow" data-depth={n.depth} data-path={f.path} style="--depth:{n.depth}">
        {#if n.hasChildren}
          <button type="button" class="twisty" aria-expanded={!collapsed} aria-label={leaf} onclick={() => folderUi.toggleCollapsed(f.path)}><Icon name="chev" size={14} /></button>
        {/if}
        <button type="button" class="fbtn big nested" class:active={on} aria-current={on ? 'page' : undefined} aria-label={f.path} onclick={() => open(f.path)}>
          <span class="fi"><Icon name={folderIcon(f, sections.ctx)} size={20} /></span><span class="fn">{leaf}</span>
          {#if collapsed && n.subtreeUnseen}<span class="badge roll">{badgeText(n.subtreeUnseen)}</span>{:else if f.unseen}<span class="badge">{badgeText(f.unseen)}</span>{/if}
        </button>
        <button type="button" class="mini" aria-label={t('newSubfolder')} onclick={() => after(newSubfolderFlow(f.path, leaf))}><Icon name="plus" size={17} /></button>
        <button type="button" class="mini" aria-label={t('renameF')} onclick={() => after(renameFolderFlow(f.path, leaf))}><Icon name="pen" size={17} /></button>
        <button type="button" class="mini danger-h" aria-label={t('delFolder')} onclick={() => after(deleteFolderFlow(f.path, leaf))}><Icon name="trash" size={17} /></button>
      </div>
    {/each}
  {/if}
  {#if sections.showMore}
    <button type="button" class="fbtn big morebtn" onclick={() => folderUi.setMore(!folderUi.moreFolders)}>
      <span class="fi"><Icon name="more" size={20} /></span><span class="fn">{folderUi.moreFolders ? t('showLess') : t('showMoreN', { n: sections.hidden })}</span>
    </button>
  {/if}
  <button type="button" class="fbtn big" onclick={() => after(newFolderFlow())}><span class="fi"><Icon name="plus" size={20} /></span><span class="fn">{t('newFolder')}</span></button>
</Sheet>

<style>
  /* @css shell/FoldersSheet start */
  .frow[data-depth] .fbtn.nested {
    padding-inline-start: calc(var(--space-3) + var(--depth) * var(--space-4))
  }

  .twisty {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: var(--control-xs);
    height: var(--control-xs);
    border: 0;
    background: none;
    cursor: pointer;
    color: var(--text-faint);
    flex: 0 0 auto
  }

  .twisty:hover {
    color: var(--text-body)
  }

  .twisty[aria-expanded=false] :global(svg) {
    transform: rotate(-90deg)
  }

  :global([dir=rtl]) .twisty[aria-expanded=false] :global(svg) {
    transform: rotate(90deg)
  }

  .badge.roll {
    opacity: .85
  }

  .fbtn {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding-block: var(--space-3);
    padding-inline: var(--space-3);
    min-block-size: var(--control-lg);
    border-radius: var(--radius-control);
    color: var(--text-body);
    width: 100%;
    text-align: start;
    transition: background var(--duration-fast) var(--ease-standard)
  }

  .fbtn:hover {
    background: var(--surface-sunken)
  }

  .fbtn.active {
    background: var(--blue-50);
    color: var(--blue-700);
    font-weight: var(--weight-semibold)
  }

  :global(html.tik-dark) .fbtn.active {
    background: #4a7cf62e;
    color: var(--blue-200)
  }

  .fbtn.big {
    padding: var(--space-3) var(--space-4);
    min-block-size: var(--control-lg)
  }

  .frow {
    display: flex;
    align-items: center;
    gap: var(--space-1)
  }

  .frow .fbtn {
    flex: 1;
    min-width: 0
  }

  .mini {
    display: inline-grid;
    place-items: center;
    min-inline-size: var(--control-sm);
    min-block-size: var(--control-sm);
    padding: var(--space-2);
    border-radius: var(--radius-xs);
    color: var(--text-faint);
    flex: none
  }

  .mini:hover {
    background: var(--surface-sunken);
    color: var(--text-strong)
  }

  .mini.danger-h:hover {
    color: var(--danger-500)
  }

  .sheet-sep {
    height: 1px;
    background: var(--border-subtle);
    margin: var(--space-2) var(--space-3)
  }

  .fi {
    width: var(--control-xs);
    display: grid;
    place-items: center
  }

  .fn {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis
  }

  .badge {
    background: var(--blue-500);
    color: var(--text-on-accent);
    border-radius: var(--radius-pill);
    display: grid;
    place-items: center;
    block-size: 20px;
    padding: 0 var(--space-2);
    font-size: var(--text-eyebrow);
    font-variant-numeric: tabular-nums;
    flex: none
  }

  .danger-h:hover {
    color: var(--danger-500)
  }

  .fbtn:active,
  .twisty:active,
  .mini:active {
    background: var(--mail-pressed)
  }
  /* @css shell/FoldersSheet end */
</style>
